import { Component } from "preact";
import { Link } from "preact-router/match";
import { connect } from "react-redux";
import accessIcon from "../../assets/accessibility/access.svg";
import assistiveListeningSystemsIcon from "../../assets/accessibility/assistiveListeningSystems.svg";
import audioDescriptionIcon from "../../assets/accessibility/audioDescription.svg";
import brailleIcon from "../../assets/accessibility/braille.svg";
import ccIcon from "../../assets/accessibility/cc.svg";
import largePrintIcon from "../../assets/accessibility/largePrint.svg";
import ocIcon from "../../assets/accessibility/oc.svg";
import signIcon from "../../assets/accessibility/sign.svg";
import telephoneIcon from "../../assets/accessibility/telephone.svg";
import typewritterIcon from "../../assets/accessibility/typewritter.svg";
import wheelchairIcon from "../../assets/accessibility/wheelchair.svg";
import ActionBar from "../../components/ActionBar";
import ActionButton from "../../components/ActionButton";
import Content from "../../components/Content";
import Page from "../../components/Page";
import { getEvent } from "../../state/actions/eventActions";
import { pageChange } from "../../utils/BrowserUtils";
import { max } from "../../utils/CssUtils";
import { css } from "emotion";
import Button from "../../components/Button";
import Router from "preact-router";

const wrapper = css({
  textAlign: "center",
  paddingTop: 24,
  h1: {
    fontSize: 20
  }
});

const content = css({
  textAlign: "left",
  display: "flex",
  justifyContent: "space-between",
  padding: "40px 50px",
  [max(768)]: {
    display: "block",
    padding: "20px 14px"
  }
});

const details = css({
  maxWidth: 480,
  dt: {
    fontWeight: 600,
    fontSize: 18
  },
  dd: {
    fontSize: 14
  }
});

const featuresBox = css({
  padding: 24,
  backgroundColor: "#fff",
  maxWidth: 360,
  width: "100%",
  border: "1px solid #dedede",
  borderRadius: 4,
  display: "block",
  height: "fit-content"
});

const navigationButtons = css({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  button: {
    minWidth: 140
  },
  [max(600)]: {
    justifyContent: "space-between",
    position: "fixed",
    padding: "12px 14px",
    width: "100vw",
    bottom: 0,
    left: 0,
    backgroundColor: "#fff",
    borderTop: "1px solid #dedede",
    zIndex: 9999999
  }
});

class Accessibility extends Component {
  constructor(props) {
    super(props);
    this.state = {
      back: `/${props.eventSlug}`
    };
  }

  componentDidMount() {
    const { event, getEvent, eventSlug, matches } = this.props;
    if (!event.data && !event.loading) {
      getEvent(eventSlug);
    }
    pageChange("Accessibility Details", event);

    let backLocation = `/${eventSlug}`;
    if (matches.source === "tickets") {
      backLocation += "/tickets";
    }
    this.setState({
      back: backLocation
    });
  }
  render({ event }) {
    const { back } = this.state;
    const features = [
      {
        name: "access",
        label: "Access",
        description:
          "This symbol indicates access for people who are blind or have low vision, best used in places such as: a guided tour, a path to a nature trail or a scent garden in a park; and a tactile tour or a museum exhibition that may be touched.",
        icon: accessIcon
      },
      {
        name: "wheelchairAccessibility",
        label: "Wheelchair Accessibility",
        description:
          "The wheelchair symbol indicates access for individuals with limited mobility, including wheelchair users. Remember that a ramped entrance is not completely accessible if there are no curb cuts, and an elevator is not accessible if it can only be reached via steps.",
        icon: wheelchairIcon
      },
      {
        name: "audioDescription",
        label: "Audio Description",
        description:
          "The wheelchair symbol indicates access for individuals with limited mobility, including wheelchair users. Remember that a ramped entrance is not completely accessible if there are no curb cuts, and an elevator is not accessible if it can only be reached via steps.",
        icon: audioDescriptionIcon
      },
      {
        name: "telephoneTypewriter",
        label: "Telephone Typewriter",
        description:
          "This device is also known as a text telephone (TT), or telecommunications device for the deaf (TDD). TTY indicates the presence of a device used with the telephone for communication with and between deaf, hard of hearing, speech impaired and/or hearing, persons.",
        icon: typewritterIcon
      },
      {
        name: "volumeControlTelephone",
        label: "Volume Control Telephone",
        description:
          "This symbol indicates the presence of telephones that have handsets with amplified sound and/or adjustable volume controls.",
        icon: telephoneIcon
      },
      {
        name: "assistiveListeningSystems",
        label: "Assistive Listening Systems",
        description:
          "These systems transmit amplified sound via hearing aids, headsets of other devices",
        icon: assistiveListeningSystemsIcon
      },
      {
        name: "signLanguageInterpretation",
        label: "Sign Language Interpretation",
        description:
          "The symbol indicates that Sign Language Interpretation is provided for a lecture, tour, film, performance, conference or other program.",
        icon: signIcon
      },
      {
        name: "accessiblePrint",
        label: "Accessible Print (18 pt. or Larger)",
        description:
          "Large print is indicated by the words: “Large Print,” printed in 18 pt. or larger text. In addition to identifying large print versions of books, pamphlets, museum guides and theater programs, you may use the symbol on conference or membership forms with large print. Sans serif or modified serif print with high contrast is important, and special attention should be paid to letter and word spacing.",
        icon: largePrintIcon
      },
      {
        name: "closedCaptioning",
        label: "Closed Captioning (CC)",
        description:
          "Closed Captioning (CC) (commonly known as subtitles) enables people who are deaf or hard of hearing to read a transcript of the audio portion of a video, film, exhibition or other presentation. As the video plays, text captions transcribe (although not always verbatim) speech and other relevant sounds.",
        icon: ccIcon
      },
      {
        name: "openedCaptioning",
        label: "Opened Captioning (OC)",
        description:
          "This symbol indicates that captions, which translate dialogue and other sounds in print, are displayed on the videotape, movie, television program or exhibit audio. Open Captioning is preferred by many, including deaf and hard-of-hearing individuals, and people whose second language is English. It also helps teach children how to read and keep sound levels to a minimum in museums and restaurants.",
        icon: ocIcon
      },
      {
        name: "brailleSymbol",
        label: "Braille Symbol",
        description:
          "This symbol indicates that printed material is available in Braille, including exhibition labeling, publications and signage.",
        icon: brailleIcon
      }
    ];
    if (!event.data) {
      return;
    }
    const accessibility = event.data.accessibility;
    const detailsList = [];

    if (accessibility.contactName) {
      detailsList.push({
        label: "Contact Name",
        value: accessibility.contactName
      });
    }
    if (accessibility.contactNumber) {
      detailsList.push({
        label: "Contact Number",
        value: accessibility.contactNumber
      });
    }

    if (accessibility.travelInstructions) {
      detailsList.push({
        label: "Travel Instructions",
        value: accessibility.travelInstructions
      });
    }

    if (accessibility.entryInstructions) {
      detailsList.push({
        label: "Entry Instructions",
        value: accessibility.entryInstructions
      });
    }

    if (accessibility.afterEntryInstructions) {
      detailsList.push({
        label: "After Entry Instructions",
        value: accessibility.afterEntryInstructions
      });
    }

    if (accessibility.hazards) {
      detailsList.push({
        label: "Hazards",
        value: accessibility.hazards
      });
    }

    if (accessibility.toiletLocation) {
      detailsList.push({
        label: "Toilet Location",
        value: accessibility.toiletLocation
      });
    }

    if (accessibility.disabledParking) {
      detailsList.push({
        label: "Accessible Parking",
        value: accessibility.disabledParking
      });
    }
    return (
      <Page background={false}>
        <Content>
          <div className={wrapper}>
            <h1>{`${event.data.name} Accessibility Details`}</h1>
            <div className={content}>
              <div className={details}>
                <dl>
                  {detailsList.map(detail => (
                    <div>
                      <dt>{detail.label}</dt>
                      <dd>{detail.value}</dd>
                    </div>
                  ))}
                </dl>
                <div class={navigationButtons}>
                  <Link href={back}>
                    <Button type="tertiary">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                      >
                        <g fill="none" fill-rule="evenodd">
                          <path
                            fill="#788391"
                            d="M15.4 7.4L14 6l-6 6 6 6 1.4-1.4-4.6-4.6z"
                          />
                          <path d="M0 0h24v24H0z" />
                        </g>
                      </svg>
                      Back
                    </Button>
                  </Link>
                  <Link href={`/${event.data.slug}/tickets`}>
                    <ActionButton style={{ marginLeft: 12 }}>
                      Get Tickets
                    </ActionButton>
                  </Link>
                </div>
              </div>
              {accessibility.features ? (
                <div className={featuresBox}>
                  <h3>Features</h3>
                  {features.map(feature => {
                    const styles = {
                      background: `url(${feature.icon}) left top no-repeat`,
                      backgroundSize: 50,
                      padding: "0 0 0 60px",
                      margin: "0 0 10px 0",
                      minHeight: 50
                    };
                    return accessibility.features &&
                      accessibility.features[feature.name] ? (
                      <h4 style={styles}>{feature.label}</h4>
                    ) : null;
                  })}
                </div>
              ) : null}
            </div>
          </div>
        </Content>
      </Page>
    );
  }
}
export default connect(
  (state, ownProps) => ({ event: state.event }),
  {
    getEvent
  }
)(Accessibility);



// WEBPACK FOOTER //
// ./routes/accessibility/Accessibility.js